import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Text,
  Grid,
  Flex,
  Tooltip,
  Icon,
} from '@wegroup/design-system';
import CarImageWithTitle from './CarImageWithTitle';
import styled from 'styled-components';
import { mq } from '../../../../../../common/src/utils/styledComponentsUtils';
import {
  IconCarFilled,
  IconSuvFilled,
  IconSportsCarFilled,
  IconCabrioFilled,
} from 'wg-fe-ui';
import { Car } from '../../../../../../../types/SMT';
import { CarInputs } from '../../../../../../../types/ProductFactory';
import { format } from 'date-fns';
import carFuelTypes from '../../../../../../sales-conversation/src/constants/carFuelTypes.json';

interface Props {
  vehicle: Car;
  carInputs: CarInputs[];
  onGoNext: () => void;
  setIsModalOpen: (arg: boolean) => void;
}

const Value: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <Text fontSize="md">{children}</Text>
);

const VehicleDataOverview: React.FC<React.PropsWithChildren<Props>> = ({
  vehicle,
  carInputs,
  onGoNext,
  setIsModalOpen,
}) => {
  const { t } = useTranslation();

  const getCarTypeForBoolean = (vehicle: Car) => {
    if (vehicle?.is_sportscar) {
      return 'SPORT';
    } else if (vehicle?.is_cabrio) {
      return 'CABRIOLET';
    } else if (vehicle?.is_jeep) {
      return 'SUV';
    } else if (vehicle?.is_coupe) {
      return 'COUPE';
    }

    return vehicle?.category || 'NONE';
  };

  const carType = getCarTypeForBoolean(vehicle);

  const iconType = (val: string) => {
    switch (val) {
      case 'CABRIOLET':
        return <IconCabrioFilled />;
      case 'SUV':
        return <IconSuvFilled />;
      case 'SPORT':
        return <IconSportsCarFilled />;
      default:
        return <IconCarFilled />;
    }
  };

  const getDisplayValue = (value: string, type: string): string => {
    if (value == null) return '-';
    switch (type) {
      case 'DATE':
        return format(new Date(value), 'dd/MM/yyyy');
      case 'CURRENCY':
        return `${Number(value).toLocaleString('nl-BE', {
          style: 'currency',
          currency: 'EUR',
        })} (${t('Excl VAT')})`;
      case 'CURRENCY_INCL_VAT':
        return `${Number(value).toLocaleString('nl-BE', {
          style: 'currency',
          currency: 'EUR',
        })} (${t('Incl VAT')})`;
      case 'FUEL_SELECT':
        return t(carFuelTypes[value]);
      default:
        return value;
    }
  };

  return (
    <Flex maxW="5xl" mx="auto" flexDir="column" alignItems="center" w="100%">
      <Stack direction={{ base: 'column', laptopL: 'row' }} spacing="6" mb="6">
        <StyledCarImageWithTitle vehicle={vehicle} />
        <Box w={{ base: '100%', tablet: 'auto' }}>
          {carType != null && (
            <>
              <Text fontWeight="bold" fontSize="lg" mb="2">
                {t('Type of vehicle')}
              </Text>
              <Flex
                alignItems="center"
                border="1px solid primary.500"
                borderRadius="8px"
                p="2"
                maxWidth="18.4rem"
              >
                <Box
                  borderRadius="full"
                  flexShrink={0}
                  bg="primary.50"
                  p="1.5"
                  w="3.5rem"
                  h="3.5rem"
                  mr="2"
                  sx={{
                    svg: {
                      width: '100%',
                      height: '100%',
                    },
                  }}
                >
                  {iconType(carType)}
                </Box>
                <p>{t(`Vehicles.category.${carType}`)}</p>
              </Flex>
            </>
          )}
          <Grid
            templateColumns={{
              base: 'repeat(2, 1fr)',
              tablet: 'repeat(3, 1fr)',
            }}
            gridGap="6"
            mt="6"
            w="100%"
          >
            {carInputs.map(({ key, name, value, type, tooltip }) => (
              <div key={key}>
                <Text fontWeight="bold" fontSize="lg" mb="2">
                  {name}
                  {tooltip && (
                    <Tooltip label={tooltip} shouldWrapChildren>
                      <Icon name="GeneralStatusInfo" />
                    </Tooltip>
                  )}
                </Text>
                <Value>
                  {getDisplayValue(
                    value != null ? value : vehicle?.[key],
                    type,
                  )}
                </Value>
              </div>
            ))}
          </Grid>
        </Box>
      </Stack>
    </Flex>
  );
};

const StyledCarImageWithTitle = styled(CarImageWithTitle)`
  width: 100%;
  max-width: 100%;
  margin-right: 0;

  & img {
    width: 100%;
  }

  ${mq.laptopL} {
    width: 50%;
  }
`;

export default VehicleDataOverview;
